import { useQueryClient } from '@tanstack/react-query';

import { useFishGroupsController_discard } from '@/services/hooks';
import type { DiscardFishGroupRequest } from '@/services/types';

import type { MutationState } from '../shared/mutation';

import { invalidateFishGroupQueries } from './_invalidateQueries';

export interface UseDiscardFishGroupResult extends MutationState {
    discard: (requestBody: DiscardFishGroupRequest) => Promise<void>;
}

interface UseDiscardFishGroupOptions {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

export const useDiscardFishGroup = (
    clientId: number,
    fishGroupId: number,
    options?: UseDiscardFishGroupOptions
): UseDiscardFishGroupResult => {
    const queryClient = useQueryClient();
    const mutation = useFishGroupsController_discard();

    const discard = async (requestBody: DiscardFishGroupRequest): Promise<void> => {
        try {
            await mutation.mutateAsync({
                clientId,
                fishGroupId,
                requestBody,
            });
            await invalidateFishGroupQueries(queryClient, clientId, fishGroupId);
            options?.onSuccess?.();
        } catch (error) {
            options?.onError?.(error);
            throw error;
        }
    };

    return {
        discard,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
